import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MessageSquareText, Truck, Phone, Star } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SaveCardSection } from "@/components/landing/SaveCardSection";
import { PHONE, PHONE_TEL } from "@/lib/site";

export default function ThankYou() {
  const [params] = useSearchParams();
  const isContact = params.get("type") === "contact";
  const bookingId = params.get("booking");

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-brand-navy px-4 py-16">
      <Seo title="Thank You | Oxford Cleaners Chandler AZ" description="Your Oxford Cleaners pickup request is in." path="/thank-you" noindex />
      <motion.div
        data-testid="thank-you-page"
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg rounded-[2rem] border border-white/10 bg-white p-8 text-center shadow-2xl sm:p-10"
      >
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.25, type: "spring", stiffness: 260, damping: 14 }}
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100"
        >
          <CheckCircle2 className="h-11 w-11 text-emerald-600" />
        </motion.span>

        <h1 data-testid="thank-you-heading" className="mt-6 font-display text-2xl font-extrabold tracking-tight text-brand-navy sm:text-3xl">
          {isContact ? "Your message is on its way!" : "Your pickup is booked — we'll text you to confirm."}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-slate-600">
          {isContact
            ? "Thanks for reaching out — a real person from our family will get back to you shortly."
            : "Keep an eye on your phone. If we don't reach you within business hours, we'll try again first thing tomorrow."}
        </p>

        <div className="mt-7 space-y-3 rounded-2xl bg-slate-50 p-5 text-left">
          <p className="flex items-center gap-3 text-sm font-semibold text-brand-navy">
            <MessageSquareText className="h-5 w-5 shrink-0 text-brand-blue" />
            We text or call to confirm your window
          </p>
          <p className="flex items-center gap-3 text-sm font-semibold text-brand-navy">
            <Truck className="h-5 w-5 shrink-0 text-brand-blue" />
            Driver grabs the bag from your doorstep
          </p>
          <p className="flex items-center gap-3 text-sm font-semibold text-brand-navy">
            <Star className="h-5 w-5 shrink-0 text-brand-blue" />
            Back fresh in about 48 hours — delivery is free
          </p>
        </div>

        <p className="mt-6 text-sm text-slate-500">
          Need to change something? Call us at{" "}
          <a data-testid="thank-you-call-link" href={PHONE_TEL} className="font-bold text-brand-blue hover:underline">
            {PHONE}
          </a>
        </p>

        {bookingId && <SaveCardSection bookingId={bookingId} />}
        <Link
          data-testid="thank-you-home-link"
          to="/"
          className="mt-6 inline-block rounded-full bg-brand-navy px-8 py-3.5 font-display text-xs font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-105"
        >
          Back to homepage
        </Link>
      </motion.div>
    </div>
  );
}
